import type { NumericColormap } from './numericColormaps';

export interface DecodedTile {
  f32: Float32Array;
  width: number;
  height: number;
}

// LRU cache for decoded float32 tile data
const CACHE_MAX = 20;
const decodedCache = new Map<string, DecodedTile>();
const cacheOrder: string[] = [];

function cacheSet(key: string, value: DecodedTile) {
  if (decodedCache.has(key)) {
    const idx = cacheOrder.indexOf(key);
    if (idx > -1) cacheOrder.splice(idx, 1);
  } else if (decodedCache.size >= CACHE_MAX) {
    const evict = cacheOrder.shift();
    if (evict) decodedCache.delete(evict);
  }
  decodedCache.set(key, value);
  cacheOrder.push(key);
}

function cacheGet(key: string): DecodedTile | undefined {
  const val = decodedCache.get(key);
  if (val) {
    const idx = cacheOrder.indexOf(key);
    if (idx > -1) cacheOrder.splice(idx, 1);
    cacheOrder.push(key);
  }
  return val;
}

const SENTINEL = -9999.0;

/**
 * Decode a .pngd tile URL into a Float32Array of rain rate values.
 * The PNG's RGBA channels encode IEEE 754 float32 bytes.
 */
export async function decodePngdTile(url: string): Promise<DecodedTile> {
  const cached = cacheGet(url);
  if (cached) return cached;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch pngd tile: ${response.status}`);
  }

  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  const { width, height } = bitmap;

  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const imageData = ctx.getImageData(0, 0, width, height);
  const f32 = new Float32Array(imageData.data.buffer);

  const result: DecodedTile = { f32, width, height };
  cacheSet(url, result);
  return result;
}

/**
 * Parse a hex color string (#RRGGBB) into [r, g, b] tuple.
 */
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/**
 * Colorize a decoded tile using the given colormap and noise floor.
 * Returns a blob URL of a colorized PNG for use in a Mapbox image source.
 */
export async function colorizeTile(
  decoded: DecodedTile,
  noiseFloor: number,
  colormap: NumericColormap,
): Promise<string> {
  const { f32, width, height } = decoded;
  const scale = colormap.scale;

  // Pre-compute RGB arrays for fast lookup
  const scaleRgb = scale.map((entry) => ({
    maxValue: entry.maxValue,
    rgb: hexToRgb(entry.color),
  }));

  const output = new Uint8ClampedArray(width * height * 4);

  for (let i = 0; i < f32.length; i++) {
    const value = f32[i];
    const offset = i * 4;

    if (value === SENTINEL || !isFinite(value) || value <= 0 || value < noiseFloor) {
      output[offset] = 0;
      output[offset + 1] = 0;
      output[offset + 2] = 0;
      output[offset + 3] = 0;
      continue;
    }

    // Find the matching color bucket
    let r = 0, g = 0, b = 0;
    for (let j = 0; j < scaleRgb.length; j++) {
      if (value <= scaleRgb[j].maxValue) {
        [r, g, b] = scaleRgb[j].rgb;
        break;
      }
      if (j === scaleRgb.length - 1) {
        [r, g, b] = scaleRgb[j].rgb;
      }
    }

    output[offset] = r;
    output[offset + 1] = g;
    output[offset + 2] = b;
    output[offset + 3] = 220; // slightly translucent to match existing radar style
  }

  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d')!;
  const imgData = new ImageData(output, width, height);
  ctx.putImageData(imgData, 0, 0);

  const blob = await canvas.convertToBlob({ type: 'image/png' });
  return URL.createObjectURL(blob);
}

/**
 * Revoke a previously created blob URL to free memory.
 */
export function revokeBlobUrl(url: string) {
  if (url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}
